// Modules
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Components
import CountDownDialog from "components/molecules/ModalContainer/CountDownModalContainer";

// Constants
import { Routes, MONTHLY_DECLARATIONS as Constants } from "constants/global";

// Type Definitions
import type { ReimbSummaryDataType } from "redux/types";

// Redux
import { acceptReimbursement, selectedVoucherGroupCode, updateSelectedVoucherGroupCodeMonthlyDeclarations } from "redux/reimbursement/reimbursementSlice";

type useMonthlyDeclarationButtonsProps = {
	reimbSummaryItem: ReimbSummaryDataType;
};

export const useMonthlyDeclarationButtons = ({ reimbSummaryItem }: useMonthlyDeclarationButtonsProps) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const selectedVoucherGroupCodeDetails = useAppSelector(selectedVoucherGroupCode);
	const [isSelectionAvailable, setIsSelectionAvailable] = useState<boolean>(false);

	useEffect(() => {
		if (selectedVoucherGroupCodeDetails) {
			Object.keys(selectedVoucherGroupCodeDetails).length === 0 ? setIsSelectionAvailable(false) : setIsSelectionAvailable(true);
		}
	}, [selectedVoucherGroupCodeDetails]);

	const handleDeclare = useCallback(() => {
		dispatch(
			updateSelectedVoucherGroupCodeMonthlyDeclarations({ voucherGroupCode: `${reimbSummaryItem.voucher_group_code}`, voucherGroupDesc: `${reimbSummaryItem.voucher_group_desc}` })
		);
	}, [dispatch, updateSelectedVoucherGroupCodeMonthlyDeclarations, reimbSummaryItem]);

	const goBack = useCallback(() => {
		dispatch(updateSelectedVoucherGroupCodeMonthlyDeclarations({}));
		navigate(Routes.MONTHLY_DECLARATIONS, { state: { reload: true } });
	}, [navigate]);

	const handleAccept = useCallback(async () => {
		try {
			const apiPayload = { reimb_summ_id: reimbSummaryItem.reimb_summ_id };
			const { errors, message } = await dispatch(acceptReimbursement(apiPayload)).unwrap();
			if (!errors?.length)
				CountDownDialog({
					timer: 3,
					title: message,
					onTimeComplete: goBack
				});
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors) {
				CountDownDialog({
					timer: 3,
					title: Constants.REQUEST_NOT_SENT,
					onTimeComplete: goBack
				});
			}
		}
	}, [goBack, dispatch, acceptReimbursement]);

	const handleReject = useCallback(() => {
		navigate(Routes.MONTHLY_DECLARATIONS_REJECT, { state: { reimbSummaryId: reimbSummaryItem.reimb_summ_id, variant: "" } });
	}, [navigate]);

	const handleEdit = useCallback(() => {
		navigate(Routes.MONTHLY_DECLARATIONS_EDIT, { state: { reimbSummaryId: reimbSummaryItem.reimb_summ_id, variant: "Edit" } });
	}, [navigate]);

	const handleView = useCallback(() => {
		navigate(Routes.MONTHLY_DECLARATIONS_EDIT, { state: { reimbSummaryId: reimbSummaryItem.reimb_summ_id, variant: "Edit", tenantRejected: reimbSummaryItem.tenant_rejected } });
	}, [navigate]);

	return { isSelectionAvailable, handleDeclare, handleAccept, handleReject, handleEdit, handleView };
};
